import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './MyReports.css';
import PageLayout from '../PageLayout/PageLayout';
import StatusBadge from '../StatusBadge/StatusBadge';
import { useReports } from '../../hooks/useReports';
import { useAccount } from '../../hooks/useAccount';
import { hasNoRuns } from '../../utils/purchaseOptions';
import { ReportSummary } from '../../services/reportsApi';
import { formatDate } from '../../utils/formatters';
import { getScoreClass } from '../../utils/scoreUtils';
import { parseError } from '../../utils/errorHandlers';
import { PAGINATION } from '../../constants';
import { MyReportsTableSkeleton } from '../Skeleton/Skeleton';
import { EmptyState, EmptyStateAction } from '../EmptyState/EmptyState';

const MyReports: React.FC = () => {
  const navigate = useNavigate();
  const { data: reports = [], isLoading, error, refetch } = useReports();
  // /reports is every user's landing page, so the account is fetched only
  // when it can change what is shown: an empty list. Users with reports make
  // no GET /account at all.
  const { data: account, isLoading: accountLoading } = useAccount({
    enabled: !isLoading && !error && reports.length === 0,
  });

  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('date');
  const [currentPage, setCurrentPage] = useState(1);

  // No history state: ReportDetails fetches the report itself, and history
  // state would keep the property address in the session history.
  const handleViewReport = (report: ReportSummary) => {
    navigate(`/report/${encodeURIComponent(report.id)}`);
  };

  // Filter reports by status
  const filteredReports = reports.filter(report =>
    filterStatus === 'all' || report.status === filterStatus
  );

  // Sort reports
  const sortedReports = [...filteredReports].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.uploadDate || 0).getTime() - new Date(a.uploadDate || 0).getTime();
      case 'status':
        return (a.status || '').localeCompare(b.status || '');
      case 'address':
        return (a.propertyAddress || '').localeCompare(b.propertyAddress || '');
      case 'score': {
        // Unscored reports (aiScore null) sort after every scored one, even 0.
        const aScored = typeof a.aiScore === 'number';
        const bScored = typeof b.aiScore === 'number';
        if (aScored !== bScored) return aScored ? -1 : 1;
        return (b.aiScore ?? 0) - (a.aiScore ?? 0);
      }
      default:
        return 0;
    }
  });

  // Reset to page 1 when filter or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, sortBy]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedReports.length / PAGINATION.PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * PAGINATION.PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGINATION.PAGE_SIZE, sortedReports.length);
  const paginatedReports = sortedReports.slice(startIndex, endIndex);

  // Generate page numbers with ellipsis truncation
  const getPageNumbers = (): (number | '...')[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | '...')[] = [1];

    if (safeCurrentPage > 3) {
      pages.push('...');
    }

    const start = Math.max(2, safeCurrentPage - 1);
    const end = Math.min(totalPages - 1, safeCurrentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (safeCurrentPage < totalPages - 2) {
      pages.push('...');
    }

    pages.push(totalPages);
    return pages;
  };

  // Loading state
  if (isLoading) {
    return (
      <PageLayout title="My Reports" subtitle="View and manage your property inspection reports">
        <div className="my-reports-loading my-reports-loading--skeleton">
          <MyReportsTableSkeleton />
          <p className="visually-hidden">Loading your reports...</p>
        </div>
      </PageLayout>
    );
  }

  // Error state
  if (error) {
    const appError = parseError(error);
    return (
      <PageLayout title="My Reports" subtitle="View and manage your property inspection reports">
        <EmptyState
          icon="bi bi-exclamation-triangle"
          iconColor="red"
          title="Failed to load reports"
          message={appError.userMessage}
          actions={appError.retryable ? [{ label: 'Try Again', onClick: () => refetch() }] : []}
        />
      </PageLayout>
    );
  }

  // Empty state
  if (reports.length === 0) {
    const noRuns = !!account && hasNoRuns(account);
    let message = 'Upload your first inspection report to get started.';
    let actions: EmptyStateAction[] = [
      { label: 'Upload Report', to: '/upload', iconClass: 'bi bi-upload me-2' },
    ];
    if (accountLoading) {
      // No actions yet, so an Upload call to action cannot flash and then
      // turn into /account. On an account error this falls back to Upload.
      actions = [];
    } else if (noRuns) {
      message = "You don't have any inspection runs yet. Add runs to upload your first report.";
      actions = [
        { label: 'See plans and credits', to: '/account', iconClass: 'bi bi-cart me-2' },
        {
          label: 'Upload Report',
          to: '/upload',
          variant: 'outline-primary',
          iconClass: 'bi bi-upload me-2',
        },
      ];
    }
    return (
      <PageLayout title="My Reports" subtitle="View and manage your property inspection reports">
        <EmptyState
          icon="bi bi-file-earmark-text"
          iconColor="blue"
          title="No Reports Yet"
          message={message}
          actions={actions}
        />
      </PageLayout>
    );
  }

  const statsCards = (
    <div className="header-stats">
      <div className="stat-card">
        <div className="stat-value">{reports.length}</div>
        <div className="stat-label">Total Reports</div>
      </div>
      <div className="stat-card">
        <div className="stat-value">{reports.filter(r => r.status === 'completed').length}</div>
        <div className="stat-label">Completed</div>
      </div>
      <div className="stat-card">
        <div className="stat-value">{reports.filter(r => r.status === 'processing').length}</div>
        <div className="stat-label">Processing</div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="My Reports"
      subtitle="View and manage your property inspection reports"
      headerActions={statsCards}
    >

      <div className="content-card">
        {/* Filters and Actions Bar */}
        <div className="reports-toolbar">
          <div className="d-flex gap-3 align-items-center">
            <div className="filter-group">
              <label htmlFor="statusFilter" className="form-label mb-0 me-2">Status:</label>
              <select
                id="statusFilter"
                className="form-select form-select-sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{width: 'auto'}}
              >
                <option value="all">All Reports</option>
                <option value="completed">Completed</option>
                <option value="processing">Processing</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="sortBy" className="form-label mb-0 me-2">Sort by:</label>
              <select
                id="sortBy"
                className="form-select form-select-sm"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{width: 'auto'}}
              >
                <option value="date">Upload Date</option>
                <option value="status">Status</option>
                <option value="address">Property Address</option>
                <option value="score">AI Score</option>
              </select>
            </div>
          </div>

          <div className="toolbar-actions">
            <Link to="/upload" className="btn btn-primary btn-sm">
              <i className="bi bi-plus-lg me-1"></i>
              Upload New Report
            </Link>
          </div>
        </div>

        {/* Reports Table */}
        <div className="reports-table-container">
          <div className="table-responsive">
            <table className="table table-hover reports-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Status</th>
                  <th>Upload Date</th>
                  <th>AI Analysis</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedReports.map((report) => (
                  <tr key={report.id}>
                    <td>
                      <div className="property-address" title={report.propertyAddress}>
                        {report.propertyAddress}
                      </div>
                    </td>
                    <td>
                      <StatusBadge status={report.status} />
                    </td>
                    <td>
                      <div className="date-info">
                        {formatDate(report.uploadDate)}
                      </div>
                    </td>
                    <td>
                      {typeof report.aiScore === 'number' && (report.issues ?? 0) > 0 ? (
                        <div className="ai-analysis">
                          <div className={`score-badge ${getScoreClass(report.aiScore)}`}>
                            <i className="bi bi-robot me-1"></i>
                            {report.aiScore}%
                          </div>
                          <div className="issues-count">
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            {report.issues} issues found
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted">
                          {report.status === 'processing'
                            ? 'Analyzing...'
                            : report.status === 'pending'
                              ? 'Upload incomplete'
                              : 'NA'}
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => handleViewReport(report)}
                      >
                        <i className="bi bi-eye me-1"></i>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="reports-pagination">
          <div className="d-flex justify-content-between align-items-center">
            <div className="pagination-info">
              {sortedReports.length > 0
                ? `Showing ${startIndex + 1}-${endIndex} of ${sortedReports.length} reports`
                : `Showing 0 of 0 reports`}
            </div>
            {sortedReports.length > PAGINATION.PAGE_SIZE && (
              <nav aria-label="Reports pagination">
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${safeCurrentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(safeCurrentPage - 1)}
                      disabled={safeCurrentPage === 1}
                      aria-label="Previous page"
                    >
                      Previous
                    </button>
                  </li>
                  {getPageNumbers().map((page, index) =>
                    page === '...' ? (
                      <li key={`ellipsis-${index}`} className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    ) : (
                      <li key={page} className={`page-item ${page === safeCurrentPage ? 'active' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(page)}
                          aria-label={`Page ${page}`}
                          aria-current={page === safeCurrentPage ? 'page' : undefined}
                        >
                          {page}
                        </button>
                      </li>
                    )
                  )}
                  <li className={`page-item ${safeCurrentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(safeCurrentPage + 1)}
                      disabled={safeCurrentPage === totalPages}
                      aria-label="Next page"
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default MyReports;
